import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  CaretRightOutlined,
} from '@ant-design/icons';

import { Session, Klass, SCENARIO_TYPES } from 'state';
import {
  classCode,
  heroOrder,
  isInProgress,
  statusMeta,
  timingLine,
} from './helpers';

type Props = {
  sessions: Session[];
  classesById: Record<number, Klass>;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const HeroCard: React.FC<{ session: Session; code: string }> = ({
  session,
  code,
}) => {
  const meta = statusMeta(session.status);
  const chipClass = meta.isRunning
    ? 'td-hchip--live'
    : meta.isPaused
      ? 'td-hchip--paused'
      : meta.isCreating
        ? 'td-hchip--creating'
        : 'td-hchip--neutral';

  return (
    <div className="td-lcard">
      <div style={{ minWidth: 0 }}>
        <div className="td-lc-class td-truncate" title={code}>
          {code}
        </div>
        <div className="td-lc-meta">
          <span className={`td-hchip ${chipClass}`}>
            {meta.isRunning && <span className="td-dot" />}
            {meta.label}
          </span>
          <span>#{session.id}</span>
          <span>
            <b>{SCENARIO_TYPES[session.scenario_type]}</b>
          </span>
          <span>{timingLine(session)}</span>
        </div>
      </div>
      {/* ACTION POLICY: hero offers only Join/Setup (no Pause/Resume/End).
          Green Join for LIVE sessions only; Creating uses a neutral Setup,
          and every other state uses a neutral Join. */}
      <Link
        // A still-creating session isn't set up yet — open it straight on the
        // Session Settings tab; everything else lands on Overview.
        to={
          meta.isCreating
            ? `/session/${session.id}/session-settings`
            : `/session/${session.id}`
        }
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button
          size="small"
          icon={<CaretRightOutlined />}
          className={meta.isRunning ? 'td-btn--live' : 'td-btn--hero-ghost'}
        >
          {meta.isCreating ? 'Setup' : 'Join'}
        </Button>
      </Link>
    </div>
  );
};

const LiveHero: React.FC<Props> = ({ sessions, classesById }) => {
  const cards = useMemo(
    () => sessions.filter(isInProgress).sort(heroOrder),
    [sessions],
  );

  const trackRef = useRef<HTMLDivElement>(null);
  const [showNav, setShowNav] = useState(false);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 2;
    setShowNav(overflow);
    setCanPrev(el.scrollLeft > 1);
    setCanNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [update, cards.length]);

  const page = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const step = Math.max(el.clientWidth * 0.85, 300);
    el.scrollBy({
      left: dir * step,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  if (!cards.length) {
    return (
      <div className="td-hero td-hero--empty">
        <div className="td-hero-empty">
          <div className="td-hero-title">Nothing in progress right now</div>
          <p>
            Running or paused sessions across your classes will show up here to
            jump back into.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="td-hero">
      <div className="td-hero-top">
        <span className="td-livebadge">
          <span className="td-dot" />
          {cards.length} ACTIVE
        </span>
        <span className="td-hero-title">Jump back in</span>
        {showNav && (
          <div className="td-hero-nav">
            <button
              type="button"
              aria-label="Previous active sessions"
              disabled={!canPrev}
              onClick={() => page(-1)}
            >
              <LeftOutlined />
            </button>
            <button
              type="button"
              aria-label="Next active sessions"
              disabled={!canNext}
              onClick={() => page(1)}
            >
              <RightOutlined />
            </button>
          </div>
        )}
      </div>
      <div className="td-livecards" ref={trackRef} onScroll={update}>
        {cards.map(s => (
          <HeroCard
            key={s.id}
            session={s}
            code={classCode(classesById[s.klass])}
          />
        ))}
      </div>
    </div>
  );
};

export default LiveHero;
